import type { UnitType } from '@bifeai/shared';

const kilos = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 3 });
const units = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });

/** Cantidad legible: gramos → "12,5 kg"; unidades → "3 u." */
export function formatQty(unitType: UnitType, qty: number): string {
  return unitType === 'WEIGHT' ? `${kilos.format(qty / 1000)} kg` : `${units.format(qty)} u.`;
}

/** Unidad del precio: "/kg" o "/u." */
export const priceUnit = (unitType: UnitType): string => (unitType === 'WEIGHT' ? '/kg' : '/u.');

/** Acepta "12,5" o "12.5" (también "1.234,5"). Devuelve null si no es un número. */
export function parseDecimal(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  let s = raw.trim().replace(/\s|\$/g, '');
  if (!s) return null;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Lo que escribe el usuario (kilos o unidades) a la unidad interna (gramos o unidades). */
export function toInternalQty(unitType: UnitType, raw: string | number): number | null {
  const n = parseDecimal(raw);
  if (n === null || n < 0) return null;
  return unitType === 'WEIGHT' ? Math.round(n * 1000) : Math.round(n);
}

/** Unidad interna a lo que se muestra en un campo editable. */
export function fromInternalQty(unitType: UnitType, qty: number): string {
  return unitType === 'WEIGHT' ? String(qty / 1000).replace('.', ',') : String(qty);
}

/** Pesos escritos ("12.500" / "12500,50") a centavos. */
export function pesosToCents(raw: string | number): number | null {
  const n = parseDecimal(raw);
  if (n === null || n < 0) return null;
  return Math.round(n * 100);
}

export function centsToPesosInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  const v = cents / 100;
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace('.', ',');
}

/** Fecha local de hoy en formato AAAA-MM-DD. */
export function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Días desde hoy hasta la fecha (negativo si ya pasó). */
export function daysUntil(iso: string): number {
  const today = new Date(`${todayIso()}T12:00:00`).getTime();
  return Math.round((new Date(`${iso}T12:00:00`).getTime() - today) / 86_400_000);
}
